import { cn } from '../../lib/cn'
import type { ButtonHTMLAttributes } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const variants: Record<Variant, string> = {
  primary:   'bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-sm hover:shadow-accent hover:-translate-y-px hover:brightness-105 active:translate-y-0 active:scale-[.99] disabled:opacity-55 disabled:shadow-none disabled:translate-y-0 disabled:hover:brightness-100',
  secondary: 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300 hover:bg-gray-50 hover:shadow-sm',
  ghost:     'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  danger:    'bg-red-600 text-white shadow-sm hover:bg-red-700 hover:-translate-y-px active:translate-y-0',
}

const sizes: Record<Size, string> = {
  sm: 'h-8  px-3.5 text-sm',
  md: 'h-9  px-4   text-sm',
  lg: 'h-11 px-6   text-base',
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  loading?: boolean
}

export function Button({ variant = 'primary', size = 'md', loading, className, children, disabled, ...props }: Props) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      )}
      {children}
    </button>
  )
}
